/**
 * Display-unit conversion. The API always returns °F, mph and inches (that
 * is what the rule thresholds are written in); conversion happens only at
 * display time, so calculations and rule evaluation never see converted
 * values.
 */

export const DEFAULT_UNITS = { temperature: 'F', speed: 'mph', precipitation: 'in' };

export const UNIT_OPTIONS = {
  temperature: [
    ['F', '°F'],
    ['C', '°C'],
  ],
  speed: [
    ['mph', 'mph'],
    ['kmh', 'km/h'],
    ['ms', 'm/s'],
  ],
  precipitation: [
    ['in', 'in'],
    ['mm', 'mm'],
  ],
};

const LABEL = Object.fromEntries(
  Object.entries(UNIT_OPTIONS).map(([kind, opts]) => [kind, Object.fromEntries(opts)]),
);

const isNum = (v) => typeof v === 'number' && Number.isFinite(v);

export function convertTemperature(f, to) {
  if (!isNum(f)) return null;
  return to === 'C' ? ((f - 32) * 5) / 9 : f;
}

export function convertSpeed(mph, to) {
  if (!isNum(mph)) return null;
  if (to === 'kmh') return mph * 1.609344;
  if (to === 'ms') return mph * 0.44704;
  return mph;
}

export function convertPrecipitation(inches, to) {
  if (!isNum(inches)) return null;
  return to === 'mm' ? inches * 25.4 : inches;
}

/** Normalise a (possibly partial or stale) units preference. */
export function resolveUnits(units) {
  const out = { ...DEFAULT_UNITS };
  for (const kind of Object.keys(DEFAULT_UNITS)) {
    const v = units?.[kind];
    if (LABEL[kind][v]) out[kind] = v;
  }
  return out;
}

/**
 * Formatter bundle for a units preference. Digits passed in are the
 * imperial digits; millimetres drop one decimal place (0.01 in ≈ 0.25 mm,
 * so two decimals of mm would imply precision the forecast doesn't have).
 */
export function makeUnits(units) {
  const u = resolveUnits(units);
  const fixed = (v, digits) => (v == null ? '—' : v.toFixed(digits));
  const precipDigits = (digits) => (u.precipitation === 'mm' ? Math.max(0, digits - 1) : digits);
  return {
    units: u,
    tempLabel: LABEL.temperature[u.temperature],
    speedLabel: LABEL.speed[u.speed],
    precipLabel: LABEL.precipitation[u.precipitation],
    /** Temperature in the other scale, for places that show both. */
    otherTempLabel: u.temperature === 'C' ? '°F' : '°C',
    temp: (f) => convertTemperature(f, u.temperature),
    otherTemp: (f) => convertTemperature(f, u.temperature === 'C' ? 'F' : 'C'),
    speed: (mph) => convertSpeed(mph, u.speed),
    precip: (inches) => convertPrecipitation(inches, u.precipitation),
    precipDigits,
    /** "81.3°F" / "27.4°C", or "—". */
    fmtTemp: (f, digits = 0) => {
      const v = convertTemperature(f, u.temperature);
      return v == null ? '—' : `${fixed(v, digits)}${LABEL.temperature[u.temperature]}`;
    },
    fmtSpeed: (mph, digits = 0) => {
      const v = convertSpeed(mph, u.speed);
      return v == null ? '—' : `${fixed(v, digits)} ${LABEL.speed[u.speed]}`;
    },
    fmtPrecip: (inches, digits = 2) => {
      const v = convertPrecipitation(inches, u.precipitation);
      return v == null ? '—' : `${fixed(v, precipDigits(digits))} ${LABEL.precipitation[u.precipitation]}`;
    },
  };
}
